import { Link } from "@tanstack/react-router";
import { createRoute } from "@tanstack/react-router";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { TrendingUp, Zap, Users, BarChart2, Building2 } from "lucide-react";
import { authenticatedRoute } from "../_authenticated";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui";
import { useCompanyGraph, useLeadSignals } from "@/hooks/useLeadSignals";
import { useAuth } from "@/contexts/useAuth";
import { usePaywallShown } from "@/hooks/usePaywallShown";
import { cn, toDisplayString, toErrorDisplayMessage, toSafeReactNode } from "@/lib/utils";

const SIGNAL_CONFIG: Record<string, {
  label: string;
  description: string;
  icon: typeof Zap;
  color: string;
}> = {
  repeat_interest: {
    label: "leadSignals.signal_repeat_interest",
    description: "leadSignals.signal_repeat_interest_desc",
    icon: TrendingUp,
    color: "text-accent bg-accent/10",
  },
  high_velocity_interest: {
    label: "leadSignals.signal_high_velocity",
    description: "leadSignals.signal_high_velocity_desc",
    icon: Zap,
    color: "text-amber-400 bg-amber-400/10",
  },
  team_interest: {
    label: "leadSignals.signal_team_interest",
    description: "leadSignals.signal_team_interest_desc",
    icon: Users,
    color: "text-blue-400 bg-blue-400/10",
  },
};

function SignalCard({ signal }: { signal: { id: string; signalType: string; createdAt: string; payload?: Record<string, unknown> } }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";
  const config = SIGNAL_CONFIG[signal.signalType];
  const Icon = config?.icon ?? BarChart2;

  return (
    <li className="flex items-start gap-3 rounded-lg border border-border bg-surface-2 p-3">
      <div className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md", config?.color ?? "text-text-muted bg-surface-3")}>
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text">
          {toSafeReactNode(config?.label ? t(config.label) : signal.signalType)}
        </p>
        <p className="text-xs text-text-muted">
          {toSafeReactNode(config?.description ? t(config.description) : "")}
          {signal.payload?.domain != null && (
            <span className="ml-1 font-mono">· {toSafeReactNode(signal.payload.domain)}</span>
          )}
        </p>
        <p className="mt-1 text-xs text-text-subtle">
          {new Date(signal.createdAt).toLocaleString(toDisplayString(locale))}
        </p>
      </div>
    </li>
  );
}

function LeadSignalsPage() {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.language || "en";
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const { data: signalsData, isLoading: signalsLoading, error: signalsError } = useLeadSignals();
  const { data: companyData, isLoading: companiesLoading, error: companiesError } = useCompanyGraph();
  const signals = signalsData?.signals ?? [];
  const companies = companyData?.companies ?? [];
  const premiumRequired =
    Boolean(signalsData?.premiumRequired) || Boolean(companyData?.premiumRequired);

  // The same flag both offers below are rendered from. See hooks/usePaywallShown.ts.
  usePaywallShown(premiumRequired, token, "premium_required");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("leadSignals.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("leadSignals.subtitle"))}</p>
      </div>

      {premiumRequired && (
        <div className="rounded-lg border border-accent/20 bg-accent/5 p-4 text-sm">
          <p className="font-medium text-text">{toSafeReactNode(t("leadSignals.premiumRequired"))}</p>
          <p className="mt-1 text-text-muted">{toSafeReactNode(t("leadSignals.premiumDescription"))}</p>
          <Link to="/billing" className="mt-2 inline-block text-accent hover:underline font-medium">
            {toSafeReactNode(t("leadSignals.upgradeNow"))} →
          </Link>
        </div>
      )}

      {/* Intent Signals */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-accent" aria-hidden />
            <CardTitle>{toSafeReactNode(t("leadSignals.signalsTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("leadSignals.signalsDescription"))}</CardDescription>
        </CardHeader>
        <CardContent>
          {premiumRequired && (
            <div className="mb-4 rounded-md border border-border bg-surface-2 p-3 text-sm text-text-muted">
              {toSafeReactNode(t("leadSignals.premiumRequired"))}
              <div className="mt-2">
                <Link to="/billing" className="text-accent hover:underline">
                  {toSafeReactNode(t("leadSignals.upgradeNow"))}
                </Link>
              </div>
            </div>
          )}
          {signalsLoading && <p className="text-sm text-text-muted">{toSafeReactNode(t("leadSignals.loading"))}</p>}
          {signalsError && <p className="text-sm text-danger">{toErrorDisplayMessage(signalsError)}</p>}
          {!signalsLoading && signals.length === 0 && !premiumRequired && (
            <div className="text-center py-8 text-text-muted">
              <TrendingUp className="mx-auto h-8 w-8 mb-3 opacity-30" aria-hidden />
              <p className="text-sm">{toSafeReactNode(t("leadSignals.noSignalsYet"))}</p>
              <p className="text-xs mt-1">{toSafeReactNode(t("leadSignals.noSignalsHint"))}</p>
            </div>
          )}
          {signals.length > 0 && (
            <ul className="space-y-2">
              {signals.map((signal) => (
                <SignalCard key={toDisplayString(signal.id)} signal={signal} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Company Graph */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-accent" aria-hidden />
            <CardTitle>{toSafeReactNode(t("leadSignals.companyGraphTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("leadSignals.companyGraphDescription"))}</CardDescription>
        </CardHeader>
        <CardContent>
          {companiesLoading && <p className="text-sm text-text-muted">{toSafeReactNode(t("leadSignals.companyGraphLoading"))}</p>}
          {companiesError && <p className="text-sm text-danger">{toErrorDisplayMessage(companiesError)}</p>}
          {!companiesLoading && companies.length === 0 && !premiumRequired && (
            <div className="text-center py-8 text-text-muted">
              <Building2 className="mx-auto h-8 w-8 mb-3 opacity-30" aria-hidden />
              <p className="text-sm">{toSafeReactNode(t("leadSignals.noCompaniesYet"))}</p>
            </div>
          )}
          {companies.length > 0 && (
            <ul className="space-y-2">
              {companies.slice(0, 20).map((company) => (
                <li
                  key={`${toDisplayString(company.domain)}-${toDisplayString(company.lastActivityAt ?? "none")}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-2 px-4 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-3 text-xs font-bold text-text-muted uppercase">
                      {toDisplayString(company.domain).slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text">{toSafeReactNode(company.name)}</p>
                      <p className="truncate text-xs text-text-muted">{toSafeReactNode(company.domain)}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-text">{toSafeReactNode(company.captureCount)}×</p>
                    <p className="text-xs text-text-muted">
                      {company.lastActivityAt
                        ? new Date(toDisplayString(company.lastActivityAt)).toLocaleDateString(toDisplayString(locale))
                        : toSafeReactNode(t("leadSignals.noActivity"))}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const leadSignalsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "lead-signals",
  component: LeadSignalsPage,
});
